import { FC, memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PaginatedLeadsDto } from '@api/apiCompat';
import { useDynamicColumns } from '@hooks/common/useDynamicColumns/useDynamicColumns';
import NoLeadsAvailableScreen from '@pages/CampaignDetails/components/Leads/components/NoLeadsAvailableScreen';
import { Page } from '@pages/LeadScrapers/types';

import { LinearProgress, SxProps } from '@mui/material';
import { GridPaginationModel, GridRowSelectionModel } from '@mui/x-data-grid';

import { StyledDataGrid } from './styled';

const CampaignsDetailsDynamicDataGrid: FC<{
  handlePageChange: (newPaginationModel: GridPaginationModel) => void;
  paginationModel: Page;
  isDataLoading: boolean;
  data?: PaginatedLeadsDto;
  isReadOnly?: boolean;
  sx?: SxProps;
  sourceName?: string;
  expandedFilters?: boolean;
  selectedLeadIds?: GridRowSelectionModel;
  handleCheckboxSelection: (selectionModel: GridRowSelectionModel) => void;
}> = ({
  data,
  handlePageChange,
  paginationModel,
  isDataLoading,
  sx,
  sourceName,
  expandedFilters,
  selectedLeadIds,
  handleCheckboxSelection,
}) => {
  const { campaignDetailsColumns, onRowClick } = useDynamicColumns({ source: sourceName });
  const { t } = useTranslation();

  const props = useMemo(
    () => ({
      rowCount: data?.total,
      loading: isDataLoading,
      pagination: !!data?.total || true,
      rows: data?.data || [],
      onRowClick,
      columns: campaignDetailsColumns,
    }),
    [data?.total, data?.data, isDataLoading, onRowClick, campaignDetailsColumns],
  );

  return (
    <StyledDataGrid
      autoHeight
      checkboxSelection
      disableColumnFilter
      disableRowSelectionOnClick
      disableColumnSelector
      disableColumnMenu
      paginationMode="client"
      showCellVerticalBorder={false}
      pageSizeOptions={[25, 50, 100]}
      onPaginationModelChange={handlePageChange}
      paginationModel={paginationModel}
      slots={{
        loadingOverlay: LinearProgress,
        noRowsOverlay: NoLeadsAvailableScreen,
      }}
      slotProps={{
        pagination: {
          labelRowsPerPage: t('common.paginationPerPageText'),
        },
      }}
      sx={sx}
      getRowId={row => row.leads_id}
      expandedFilters={expandedFilters}
      onRowSelectionModelChange={handleCheckboxSelection}
      rowSelectionModel={selectedLeadIds}
      {...props}
    />
  );
};

export default memo(CampaignsDetailsDynamicDataGrid);
